import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, merge, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';

const LAST_ACTIVITY_KEY = 'lastActivity';

@Injectable({
  providedIn: 'root',
})
export class UserActivityService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private lastActivity = this.getStoredActivity();

  constructor(private ngZone: NgZone) {
    this.trackActivity();
    this.listenForStorageChanges();
  }

  /**
   * Track user activity events and update lastActivity timestamp
   * @private
   */
  private trackActivity(): void {
    const activityEvents$ = merge(
      // fromEvent(document, 'mousemove').pipe(debounceTime(5000)),
      fromEvent(document, 'keydown').pipe(debounceTime(1000)),
      fromEvent(document, 'click'),
      fromEvent(window, 'focus')
    );

    // We run this outside zone cause tracking user only changes variable in localStorage/memory,
    // and we are running forceRefreshSession in the background so there's no need to trigger
    // change detection system
    this.ngZone.runOutsideAngular(() => {
      activityEvents$
        .pipe(
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          const now = Date.now();
          console.debug(`[UserActivityService] Updating last activity: ${Date.now()}`);
          this.lastActivity = now;
          localStorage.setItem(LAST_ACTIVITY_KEY, now.toString());
        });
    });
  }

  /**
   * Get last activity from local storage or current time if not set
   * @private
   */
  private getStoredActivity(): number {
    const stored = localStorage.getItem(LAST_ACTIVITY_KEY);
    return stored ? +stored : Date.now();
  }

  /**
   * Listen for storage changes to keep lastActivity in sync across tabs
   * @private
   */
  private listenForStorageChanges(): void {
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event.key === LAST_ACTIVITY_KEY && !!event.newValue)
      )
      .subscribe((event) => {
        this.lastActivity = +event.newValue!;
      });
  }

  /**
   * Check if user was active in the last X minutes
   * @param minutes
   */
  wasActiveInLastMinutes(minutes: number): boolean {
    const now = Date.now();

    // check if lastActivity is NOT forged
    if (this.lastActivity > now) {
      return false;
    }

    return now - this.lastActivity <= minutes * 60_000;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
